import { useEffect, useRef, useState } from 'react';

import { Constants, useMediaDevice } from '@videosdk.live/react-sdk';

import { MeetingDetailsScreen } from '../MeetingDetailsScreen';
import ConfirmBox from '../ConfirmBox';
import useIsMobile from '../../hooks/useIsMobile';
import useMediaStream from '../../hooks/useMediaStream';
import MicPermissionDenied from '../../icons/MicPermissionDenied';
import CameraPermissionDenied from '../../icons/CameraPermissionDenied';
import { useMeetingAppContext } from '../../MeetingAppContextDef';

export function JoiningScreen({
  participantName,
  setParticipantName,
  setMicOn,
  setWebcamOn,
  onClickStartMeeting,
  setCustomAudioStream,
  setCustomVideoStream,
  micOn,
  webcamOn,
}) {
  const bucketUrl = process.env.REACT_APP_S3_BUCKET_URL;
  const {
    selectedWebcam,
    selectedMic,
    setSelectedMic,
    setSelectedWebcam,
    setSelectedSpeaker,
    isCameraPermissionAllowed,
    isMicrophonePermissionAllowed,
    setIsCameraPermissionAllowed,
    setIsMicrophonePermissionAllowed,
  } = useMeetingAppContext();
  const isMobile = useIsMobile();
  const { getVideoTrack, getAudioTrack } = useMediaStream();

  const [audioTrack, setAudioTrack] = useState(null);
  const [videoTrack, setVideoTrack] = useState(null);
  const [dlgMuted, setDlgMuted] = useState(false);
  const [dlgDevices, setDlgDevices] = useState(false);

  const videoPlayerRef = useRef();
  const audioPlayerRef = useRef();
  const videoTrackRef = useRef();
  const audioTrackRef = useRef();
  const permissonAvaialble = useRef();
  const webcamRef = useRef();
  const micRef = useRef();

  useEffect(() => {
    webcamRef.current = webcamOn;
  }, [webcamOn]);

  useEffect(() => {
    micRef.current = micOn;
  }, [micOn]);

  useEffect(() => {
    permissonAvaialble.current = {
      isCameraPermissionAllowed,
      isMicrophonePermissionAllowed,
    };
  }, [isCameraPermissionAllowed, isMicrophonePermissionAllowed]);

  useEffect(() => {
    if (micOn) {
      audioTrackRef.current = audioTrack;
      startMuteListener();
    }
  }, [micOn, audioTrack]);

  useEffect(() => {
    if (micOn) {
      // Close the existing audio track if there's a new one
      if (audioTrackRef.current && audioTrackRef.current !== audioTrack) {
        audioTrackRef.current.stop();
      }

      audioTrackRef.current = audioTrack;

      if (audioTrack) {
        const audioSrcObject = new MediaStream([audioTrack]);
        if (audioPlayerRef.current) {
          audioPlayerRef.current.srcObject = audioSrcObject;
          audioPlayerRef.current
            .play()
            .catch((error) => console.error('audio play error', error));
        }
      } else {
        if (audioPlayerRef.current) {
          audioPlayerRef.current.srcObject = null;
        }
      }
    }
  }, [micOn, audioTrack]);

  useEffect(() => {
    if (webcamOn) {
      // Close the existing video track if there's a new one
      if (videoTrackRef.current && videoTrackRef.current !== videoTrack) {
        videoTrackRef.current.stop(); // Stop the existing video track
      }

      videoTrackRef.current = videoTrack;

      var isPlaying =
        videoPlayerRef.current.currentTime > 0 &&
        !videoPlayerRef.current.paused &&
        !videoPlayerRef.current.ended &&
        videoPlayerRef.current.readyState >
          videoPlayerRef.current.HAVE_CURRENT_DATA;

      if (videoTrack) {
        const videoSrcObject = new MediaStream([videoTrack]);

        if (videoPlayerRef.current) {
          videoPlayerRef.current.srcObject = videoSrcObject;
          if (videoPlayerRef.current.paused && !isPlaying) {
            videoPlayerRef.current
              .play()
              .catch((error) => console.error('video play error', error));
          }
        }
      } else {
        if (videoPlayerRef.current) {
          videoPlayerRef.current.srcObject = null;
        }
      }
    }
  }, [webcamOn, videoTrack]);

  useEffect(() => {
    getCameraDevices();
  }, [isCameraPermissionAllowed]);

  useEffect(() => {
    getAudioDevices();
  }, [isMicrophonePermissionAllowed]);

  useEffect(() => {
    checkMediaPermission();
    return () => {};
  }, []);

  const getDefaultMediaTracks = async ({ mic, webcam }) => {
    if (mic) {
      const stream = await getAudioTrack({
        micId: selectedMic.id,
      });
      setCustomAudioStream(stream);
      const audioTracks = stream?.getAudioTracks();
      const audioTrack = audioTracks?.length ? audioTracks[0] : null;
      setAudioTrack(audioTrack);
    }

    if (webcam) {
      const stream = await getVideoTrack({
        webcamId: selectedWebcam?.id,
      });
      setCustomVideoStream(stream);
      const videoTracks = stream?.getVideoTracks();
      const videoTrack = videoTracks.length ? videoTracks[0] : null;
      setVideoTrack(videoTrack);
    }
  };

  const startMuteListener = async () => {
    const currentAudioTrack = audioTrackRef.current;

    if (currentAudioTrack) {
      if (currentAudioTrack.muted) {
        setDlgMuted(true);
      }

      currentAudioTrack.addEventListener('mute', (ev) => {
        setDlgMuted(true);
      });
    }
  };

  const requestAudioVideoPermission = async (mediaType) => {
    try {
      const permission = await requestPermission(mediaType);

      if (mediaType === Constants.permission.AUDIO) {
        const isAudioAllowed = permission.get(Constants.permission.AUDIO);
        setIsMicrophonePermissionAllowed(isAudioAllowed);
        if (isAudioAllowed) {
          setMicOn(true);
          await getDefaultMediaTracks({ mic: true, webcam: false });
        }
      }

      if (mediaType === Constants.permission.VIDEO) {
        const isVideoAllowed = permission.get(Constants.permission.VIDEO);
        setIsCameraPermissionAllowed(isVideoAllowed);
        if (isVideoAllowed) {
          setWebcamOn(true);
          await getDefaultMediaTracks({ mic: false, webcam: true });
        }
      }
    } catch (ex) {
      console.error('Error in requestPermission ', ex);
    }
  };

  const onDeviceChanged = () => {
    getCameraDevices();
    getAudioDevices();
    getDefaultMediaTracks({ mic: micRef.current, webcam: webcamRef.current });
  };

  const {
    checkPermissions,
    getCameras,
    getMicrophones,
    requestPermission,
    getPlaybackDevices,
  } = useMediaDevice({ onDeviceChanged });

  const checkMediaPermission = async () => {
    try {
      const checkAudioVideoPermission = await checkPermissions();
      const cameraPermissionAllowed = checkAudioVideoPermission.get(
        Constants.permission.VIDEO,
      );
      const microphonePermissionAllowed = checkAudioVideoPermission.get(
        Constants.permission.AUDIO,
      );

      setIsCameraPermissionAllowed(cameraPermissionAllowed);
      setIsMicrophonePermissionAllowed(microphonePermissionAllowed);

      if (microphonePermissionAllowed) {
        setMicOn(true);
        getDefaultMediaTracks({ mic: true, webcam: false });
      } else {
        await requestAudioVideoPermission(Constants.permission.AUDIO);
      }
      if (cameraPermissionAllowed) {
        setWebcamOn(true);
        getDefaultMediaTracks({ mic: false, webcam: true });
      } else {
        await requestAudioVideoPermission(Constants.permission.VIDEO);
      }
    } catch (error) {
      // For firefox, it will request audio and video simultaneously.
      await requestAudioVideoPermission();
      console.error(error);
    }
  };

  async function getCameraDevices() {
    try {
      if (permissonAvaialble.current?.isCameraPermissionAllowed) {
        let webcams = await getCameras();
        setSelectedWebcam({
          id: webcams[0]?.deviceId,
          label: webcams[0]?.label,
        });
      }
    } catch (err) {
      console.error('Error in getting camera devices', err);
    }
  }

  async function getAudioDevices() {
    try {
      if (permissonAvaialble.current?.isMicrophonePermissionAllowed) {
        let mics = await getMicrophones();
        let speakers = await getPlaybackDevices();
        const hasMic = mics.length > 0;
        if (hasMic) {
          startMuteListener();
        }

        setSelectedSpeaker({
          id: speakers[0]?.deviceId,
          label: speakers[0]?.label,
        });
        await setSelectedMic({ id: mics[0]?.deviceId, label: mics[0]?.label });
      }
    } catch (err) {
      console.error('Error in getting audio devices', err);
    }
  }

  return (
    <div className='fixed inset-0'>
      <div className='overflow-y-auto flex flex-col flex-1 h-screen bg-purple-900'>
        <header className='w-full py-4 bg-purple-900 flex items-center justify-center'>
          <img
            src={`${bucketUrl}/logotipo2_e6be46630f.png`}
            alt='Logo'
            className='mt-4'
          />
        </header>
        <div className='flex flex-1 flex-col md:flex-row items-center justify-center md:m-[72px] m-16'>
          <div className='container grid  md:grid-flow-col grid-flow-row '>
            <div className='grid grid-cols-12'>
              <div className='md:col-span-7 2xl:col-span-6 col-span-12'>
                <div className='flex items-center justify-center p-1.5 sm:p-4 lg:p-6'>
                  <div
                    className='w-full relative'
                    style={{ height: isMobile ? '45vh' : '55vh' }}
                  >
                    <video
                      autoPlay
                      playsInline
                      muted
                      ref={videoPlayerRef}
                      controls={false}
                      style={{
                        backgroundColor: '#1c1c1c',
                        transform: 'scaleX(-1)',
                        WebkitTransform: 'scaleX(-1)',
                      }}
                      className={
                        'rounded-[10px] h-full w-full object-cover flex items-center justify-center flip'
                      }
                    />

                    <div className='absolute xl:bottom-6 bottom-4 left-0 right-0'>
                      <div className='container grid grid-flow-col space-x-4 items-center justify-center md:-m-2'>
                        {!isMicrophonePermissionAllowed && (
                          <MicPermissionDenied />
                        )}

                        {!isCameraPermissionAllowed && (
                          <CameraPermissionDenied />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='md:col-span-5 2xl:col-span-6 col-span-12 md:relative'>
                <div className='flex flex-1 flex-col items-center justify-center xl:m-16 lg:m-6 md:mt-9 lg:mt-14 xl:mt-20 mt-3 md:absolute md:left-0 md:right-0 md:top-0 md:bottom-0'>
                  <MeetingDetailsScreen
                    participantName={participantName}
                    setParticipantName={setParticipantName}
                    onJoin={onClickStartMeeting}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmBox
        open={dlgMuted}
        successText='OKAY'
        onSuccess={() => {
          setDlgMuted(false);
        }}
        title='System mic is muted'
        subTitle="You're default microphone is muted, please unmute it or increase audio
            input volume from system settings."
      />

      <ConfirmBox
        open={dlgDevices}
        successText='DISMISS'
        onSuccess={() => {
          setDlgDevices(false);
        }}
        title='Mic or webcam not available'
        subTitle='Please connect a mic and webcam to speak and share your video in the meeting. You can also join without them.'
      />
    </div>
  );
}
